import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  IconButton,
  Divider,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DescriptionIcon from "@mui/icons-material/Description";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ArticleIcon from "@mui/icons-material/Article";
import DeleteIcon from "@mui/icons-material/Delete";

const ViewFileDetailsModal = ({ open, onClose, data }) => {
  if (!data) return null;

  const {
    name,
    type,
    size,
    uploadedAt,
    uploadedBy,
    status,
    downloads,
    viewDetailsData,
    onRemoveUsedBy,
  } = data;

  const getFileIcon = (fileType) => {
    switch (fileType) {
      case "PDF":
        return <PictureAsPdfIcon sx={{ color: "#D32F2F", fontSize: 48 }} />;
      case "DOCX":
        return <ArticleIcon sx={{ color: "#1976D2", fontSize: 48 }} />;
      case "TXT":
      case "MD":
      case "CSV":
        return <DescriptionIcon sx={{ color: "#666", fontSize: 48 }} />;
      default:
        return <DescriptionIcon sx={{ color: "#666", fontSize: 48 }} />;
    }
  };

  const getStatusColor = (fileStatus) => {
    const colors = {
      Indexed: "success",
      Processing: "warning",
      Failed: "error",
    };
    return colors[fileStatus] || "default";
  };

  const handleRemoveAssistant = (assistantName) => {
    if (onRemoveUsedBy && viewDetailsData?.fileId) {
      onRemoveUsedBy(viewDetailsData.fileId, assistantName);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography variant="h6" fontWeight={600}>
            File Details
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {/* File Icon and Name */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
          {getFileIcon(type)}
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              {name}
            </Typography>
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <Chip
                label={type}
                size="small"
                sx={{
                  backgroundColor: type === "PDF" ? "#FFEBEE" : "#E3F2FD",
                  color: type === "PDF" ? "#C62828" : "#1565C0",
                  fontWeight: 600,
                }}
              />
              <Chip
                label={status}
                size="small"
                color={getStatusColor(status)}
                sx={{ fontWeight: 500 }}
              />
            </Box>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* File Information */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            File Information
          </Typography>
          <Box sx={{ mt: 2 }}>
            <InfoRow label="Size" value={size} />
            <InfoRow label="Uploaded At" value={uploadedAt} />
            <InfoRow label="Uploaded By" value={uploadedBy} />
            <InfoRow label="Downloads" value={downloads} />
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Used By Section */}
        <Box>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Used By Assistants
          </Typography>

          {!viewDetailsData ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 2 }}>
              <CircularProgress size={20} />
              <Typography variant="body2" color="text.secondary">
                Loading usage details...
              </Typography>
            </Box>
          ) : viewDetailsData.usedBy && viewDetailsData.usedBy.length > 0 ? (
            <Box sx={{ mt: 2 }}>
              {viewDetailsData.usedBy.map((assistantName, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    p: 1.5,
                    mb: 1,
                    backgroundColor: "#F9FAFB",
                    borderRadius: 1,
                    border: "1px solid #E0E0E0",
                    "&:hover": {
                      backgroundColor: "#F3F4F6",
                    },
                  }}
                >
                  <Typography variant="body2" fontWeight={500}>
                    {assistantName}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveAssistant(assistantName)}
                    sx={{ color: "error.main" }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </Box>
          ) : (
            <Box
              sx={{
                py: 3,
                textAlign: "center",
                backgroundColor: "#F9FAFB",
                borderRadius: 1,
                mt: 2,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                This file is not currently used by any assistants
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Helper component for info rows
const InfoRow = ({ label, value }) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      py: 1,
      borderBottom: "1px solid #F0F0F0",
      "&:last-child": {
        borderBottom: "none",
      },
    }}
  >
    <Typography variant="body2" color="text.secondary">
      {label}:
    </Typography>
    <Typography variant="body2" fontWeight={500}>
      {value}
    </Typography>
  </Box>
);

export default ViewFileDetailsModal;
